import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ActionItemStatuses, actionItemStatusLabel } from '../../../../utils/vendorActionItems';


const MenuButton = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${(p) => p.theme.colors.Gray500};
  cursor: pointer;
  font-size: 14px;
  line-height: 14px;
  padding: 2px 6px;

  &:hover, &:focus-visible { background: #F2F4F7; border-color: ${(p) => p.theme.colors.Gray300}; }
`;

const Menu = styled.div`
  background: white;
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 8px;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.1);
  display: flex;
  flex-direction: column;
  min-width: 160px;
  padding: 4px;
  position: fixed;
  transform: translateX(-100%);
  z-index: 100;
`;

const MenuItem = styled.button<{ $current: boolean }>`
  background: ${(p) => (p.$current ? '#F9F5FF' : 'transparent')};
  border: none;
  border-radius: 6px;
  color: ${(p) => p.theme.colors.Gray700};
  cursor: pointer;
  font-size: 12px;
  padding: 8px 10px;
  text-align: left;

  &:hover, &:focus-visible { background: #F2F4F7; outline: none; }
`;

const MenuHeader = styled.div`
  color: #98A2B3;
  font-size: 10px;
  font-weight: 600;
  padding: 6px 10px 2px;
  text-transform: uppercase;
`;

type Props = {
  status: api.VendorActionItemStatus;
  onMove: (status: api.VendorActionItemStatus) => void;
};

// Menu and keyboard alternative to dragging an item between status columns.
// The menu is portaled and fixed-positioned under the button so it floats above clipped or dimmed containers
// (e.g. a closed item's faded row in the Enablement expanded row) instead of growing or inheriting from them.
export default function MoveToMenu({ status, onMove }: Props): React.ReactElement {
  // Viewport position of the open menu's top-right corner; undefined when closed.
  const [anchor, setAnchor] = useState<{ top: number; right: number } | undefined>();
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const open = anchor !== undefined;

  // Not mesa-ui's useClickOutside: it listens for `click`, which this menu (and every other one in the list) stops
  // from propagating so the card doesn't open, so opening a second menu would leave the first one open.
  // A fixed menu would drift from its button on scroll, so scrolling or resizing closes it.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setAnchor(undefined);
    const closeOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !menuRef.current?.contains(target)) close();
    };
    document.addEventListener('mousedown', closeOutside);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchor(open ? undefined : { top: rect.bottom + 4, right: rect.right });
  };

  // React events still bubble through the portal to this wrapper, which keeps menu clicks from opening the card.
  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <MenuButton aria-label="Move to…" title="Move to…" onClick={toggle}>⋯</MenuButton>
      {anchor && createPortal(
        <Menu ref={menuRef} role="menu" style={{ top: anchor.top, left: anchor.right }} onKeyDown={(e) => { if (e.key === 'Escape') setAnchor(undefined); }}>
          <MenuHeader>Move to…</MenuHeader>
          {ActionItemStatuses.map((s) => (
            <MenuItem
              key={s}
              role="menuitem"
              $current={s === status}
              autoFocus={s === status}
              onClick={() => { setAnchor(undefined); if (s !== status) onMove(s); }}
            >
              {s === status ? '✓ ' : ''}
              {actionItemStatusLabel(s)}
            </MenuItem>
          ))}
        </Menu>,
        document.body,
      )}
    </div>
  );
}
